/* eslint-disable tss-unused-classes/unused-classes */

import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { institutionLoginsSelectors, institutionIconTypes } from 'app-components/flux/institution-logins';
import type { institutionsTypes } from 'app-components/flux/institutions';
import { getLogger } from 'app-components/utils/core';

import bankIcon from 'app-components/assets/QIllustration/FiBuilding.svg';

const logger = getLogger('InstitutionIcon');
import QIllustration from 'components/QIllustration';
import { useAppSelector } from 'hooks/redux';

export const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  containerForButtonStyle: {
    height: '64px',
    width: '102px',
    borderRadius: '6px',
  },
  // used with {size}ContainerStyle
  mdContainerForIconStyle: {
    border: `1px ${theme.palette.border.main} solid`,
    borderRadius: 16,
    height: '64px',
    width: '64px',
  },
  // used with {size}ContainerStyle
  smContainerForIconStyle: {
    border: `1px ${theme.palette.border.main} solid`,
    borderRadius: 12,
    height: '48px',
    width: '48px',
  },
  xsContainerForIconStyle: {
    border: `1px ${theme.palette.border.main} solid`,
    borderRadius: 10,
    height: '42px',
    width: '42px',
  },
  genericImageButtonStyle: {
    backgroundColor: theme.palette.background.layer1,
    border: '1px solid rgba(151,151,151,0.4)',
  },
  img: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    minHeight: '1px',
  },
  round: {
    borderRadius: '50%',
  },
}));

type sizeOptions = 'md' | 'sm' | 'xs';

type DisplayProps = {
  size?: sizeOptions;
  sizePixels?: number;
  showEmptyInstitutionIcon?: boolean;
  square?: boolean;
  sx?: SxProps<Theme>;
};

type InstitutionIconProps = DisplayProps & institutionIconTypes.InstitutionIconInput;

const ILLUSTRATION_SIZE_MAP = { xs: 42, sm: 48, md: 64 } as const;

const InstitutionIconImage = ({
  iconSource,
  illustrationPixels,
  imgClassName,
}: {
  iconSource: institutionIconTypes.InstitutionIconSource;
  illustrationPixels: number;
  imgClassName: string;
}) => {
  switch (iconSource.kind) {
    case 'illustration':
      return (
        <QIllustration name={iconSource.illustrationName} width={illustrationPixels} height={illustrationPixels} />
      );
    case 'logoUrl':
      return <img alt={iconSource.name} src={iconSource.url} className={imgClassName} />;
    case 'base64':
      return <img alt={iconSource.name} src={iconSource.data} className={imgClassName} />;
    case 'fallback':
      return <img alt='Generic Bank Icon' src={bankIcon} className={imgClassName} />;
    default: {
      const exhaustive: never = iconSource;
      logger.error('Unhandled InstitutionIconSource kind:', exhaustive);
      return <img alt='Generic Bank Icon' src={bankIcon} className={imgClassName} />;
    }
  }
};

// Opt to pass in an accountId if available
const InstitutionIcon = (props: InstitutionIconProps) => {
  const { size = 'md', sizePixels, showEmptyInstitutionIcon = true, square, sx } = props;
  const { classes, cx } = useStyles();

  const iconSource = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionIconSource(
      state,
      'accountId' in props
        ? { accountId: props.accountId }
        : 'institutionLoginId' in props
          ? { institutionLoginId: props.institutionLoginId }
          : { institution: props.institution },
    ),
  );

  if (iconSource.kind === 'fallback' && !showEmptyInstitutionIcon) {
    return null;
  }

  const logoStyle = institutionIconTypes.getLogoStyle(iconSource);
  const iconClassNames: string[] = [];

  if (iconSource.kind === 'fallback') {
    iconClassNames.push(classes.genericImageButtonStyle);
  }

  if (logoStyle === 'BUTTON') {
    iconClassNames.push(classes.containerForButtonStyle);
  } else {
    iconClassNames.push(classes[`${size}ContainerForIconStyle`]);
    if (!square) {
      iconClassNames.push(classes.round);
    }
  }

  const illustrationPixels = sizePixels ?? ILLUSTRATION_SIZE_MAP[size];

  return (
    <Box
      className={cx(classes.container, iconClassNames)}
      style={sizePixels ? { height: sizePixels, width: sizePixels } : undefined}
      sx={sx}
    >
      <InstitutionIconImage
        iconSource={iconSource}
        illustrationPixels={illustrationPixels}
        imgClassName={classes.img}
      />
    </Box>
  );
};

export const InstitutionIconSize = (institution?: institutionsTypes.Institution, size: sizeOptions = 'md') => {
  if (institution?.logoStyle === 'BUTTON') {
    return { height: 64, width: 102 };
  }
  return size === 'sm' ? { height: 48, width: 48 } : { height: 64, width: 64 };
};

export default InstitutionIcon;
